import { useState } from 'react';
import { Plus, Trash, X } from '@phosphor-icons/react';
import { ImmoButton, InnenKarte, KartenTitel, TextFeld, FeldRaster } from '../../../../../components/immobilien/ui';
import { eur } from '../../../../../lib/immobilien/format';
import { leseZahl } from '../../../../../lib/immobilien/mietverhaeltnisFormular';
import {
  POSITION_ARTEN, gesamtmiete, positionFehler, standFehler, summePositionen, type KostenPosition, type Mietstand, type PositionArt,
} from '../../../../../lib/mieter/mietstaende';

const zahlText = (n: number) => (n ? String(n).replace('.', ',') : '');

/**
 * Eingabe für EINEN Mietstand (neue Mietänderung oder Stand bearbeiten) – dieselben Felder und
 * Prüfungen wie bisher (Kaltmiete, Betriebskosten, Heizkosten, gültig ab, Positionen).
 * Schreibt nichts selbst; Übernehmen speichert der Bereich.
 */
export function MietstandEditor({ stand, istNeu, andereStaende, onAbbrechen, onUebernehmen, titel, uebernehmenLabel = 'Übernehmen', laeuft = false }: {
  stand: Mietstand;
  istNeu: boolean;
  andereStaende: readonly Mietstand[];
  onAbbrechen: () => void;
  onUebernehmen: (s: Mietstand) => void;
  /** Abweichender Kartentitel (Mieter-Panel: „Miete anpassen“) */
  titel?: string;
  uebernehmenLabel?: string;
  /** Speichern läuft – Knopf gesperrt */
  laeuft?: boolean;
}) {
  const [kalt, setKalt] = useState(zahlText(stand.kaltmiete));
  const [bk, setBk] = useState(zahlText(stand.betriebskosten));
  const [hk, setHk] = useState(zahlText(stand.heizkosten));
  const [ab, setAb] = useState(stand.gueltigAb);
  const [positionen, setPositionen] = useState<(KostenPosition & { betragText: string })[]>(
    (stand.positionen ?? []).map(p => ({ ...p, betragText: zahlText(p.betrag) })));
  const [fehler, setFehler] = useState<string | null>(null);

  const fertigePositionen: KostenPosition[] = positionen.map(({ betragText, ...p }) => ({ ...p, betrag: leseZahl(betragText) ?? 0 }));
  const weitere = summePositionen(fertigePositionen);
  const entwurf: Mietstand = {
    ...stand, gueltigAb: ab, kaltmiete: leseZahl(kalt) ?? 0, betriebskosten: leseZahl(bk) ?? 0, heizkosten: leseZahl(hk) ?? 0,
    weitereKosten: weitere, positionen: fertigePositionen,
  };

  const aendere = (id: string, teil: Partial<KostenPosition & { betragText: string }>) =>
    setPositionen(liste => liste.map(p => (p.id === id ? { ...p, ...teil } : p)));

  const uebernehmen = () => {
    const f = standFehler(entwurf, [...andereStaende]);
    if (f) { setFehler(f); return; }
    for (const p of fertigePositionen) { const pf = positionFehler(p); if (pf) { setFehler(pf); return; } }
    onUebernehmen(entwurf);
  };

  return (
    <InnenKarte style={{ boxShadow: 'inset 0 0 0 2px var(--acc)' }}>
      <KartenTitel rechts={<button type="button" aria-label="Abbrechen" onClick={onAbbrechen} className="flex items-center justify-center cursor-pointer"
        style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}><X size={14} weight="bold" /></button>}>
        {titel ?? (istNeu ? 'Neue Mietänderung' : 'Mietstand bearbeiten')}
      </KartenTitel>
      <FeldRaster minBreite={150}>
        <TextFeld label="Kaltmiete" wert={kalt} onChange={setKalt} inputMode="decimal" suffix="€" />
        <TextFeld label="Betriebskosten" wert={bk} onChange={setBk} inputMode="decimal" suffix="€" />
        <TextFeld label="Heizkosten" wert={hk} onChange={setHk} inputMode="decimal" suffix="€" />
        <TextFeld label="Gültig ab" type="date" wert={ab} onChange={setAb} />
      </FeldRaster>
      <div className="flex flex-col" style={{ gap: 8 }}>
        <div className="flex items-center justify-between" style={{ gap: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Weitere Kosten</span>
          <span className="immo-zahl" style={{ fontSize: 14, fontWeight: 800 }}>{eur(weitere)}</span>
        </div>
        {positionen.length === 0 && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Keine Position – z. B. Stellplatz, Garage oder Keller hinzufügen.</span>}
        {positionen.map(p => {
          const braucht = POSITION_ARTEN.find(a => a.wert === p.art)?.brauchtBezeichnung;
          return (
            <div key={p.id} className="flex flex-wrap items-end" style={{ gap: 8 }}>
              <label className="flex flex-col" style={{ gap: 6, minWidth: 150 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Art</span>
                <select value={p.art} onChange={e => aendere(p.id, { art: e.target.value as PositionArt })}
                  style={{ height: 50, borderRadius: 15, padding: '0 12px', border: 'none', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
                  {POSITION_ARTEN.map(a => <option key={a.wert} value={a.wert}>{a.label}</option>)}
                </select>
              </label>
              <div style={{ flex: '1 1 160px' }}>
                <TextFeld label="Bezeichnung" wert={p.bezeichnung ?? ''} onChange={v => aendere(p.id, { bezeichnung: v })}
                  placeholder={p.art === 'raum' ? 'Wo? z. B. Dachgeschoss links' : braucht ? 'Wofür?' : 'optional'} />
              </div>
              <div style={{ width: 130 }}>
                <TextFeld label="Betrag" wert={p.betragText} onChange={v => aendere(p.id, { betragText: v })} inputMode="decimal" suffix="€" />
              </div>
              <ImmoButton art="bad" groesse={44} icon={Trash} aria-label="Position entfernen" title="Position entfernen"
                onClick={() => setPositionen(liste => liste.filter(x => x.id !== p.id))} />
            </div>
          );
        })}
        <ImmoButton groesse={36} icon={Plus} style={{ alignSelf: 'flex-start' }}
          onClick={() => setPositionen(liste => [...liste, { id: `p-${Date.now()}`, art: 'stellplatz', bezeichnung: null, betrag: 0, betragText: '' }])}>
          Position hinzufügen
        </ImmoButton>
      </div>
      {fehler && <span role="alert" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}
      <div className="flex items-center justify-between flex-wrap" style={{ gap: 10, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
        <span style={{ fontSize: 14, color: 'var(--ink2)' }}>Gesamtmiete <strong className="immo-zahl" style={{ color: 'var(--ink)' }}>{eur(gesamtmiete(entwurf))}</strong></span>
        <div className="flex" style={{ gap: 8 }}>
          <ImmoButton onClick={onAbbrechen}>Abbrechen</ImmoButton>
          <ImmoButton art="akzent" disabled={laeuft} onClick={uebernehmen}>{uebernehmenLabel}</ImmoButton>
        </div>
      </div>
    </InnenKarte>
  );
}
